"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HoaTreeCareHero() {
  const headlineWords = ["Preserving", "Nature's", "Grand"];
  const {
    ref: metaRef,
    isVisible: metaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: kanjiRef,
    isVisible: kanjiVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2869" id="hoa-tree-care-hero" className="relative min-h-screen w-full bg-background text-foreground overflow-hidden">
      {}
      <div ref={kanjiRef} className={`pointer-events-none absolute -bottom-12 -left-8 md:bottom-0 md:left-4 select-none transition-all duration-[1400ms] ease-out ${kanjiVisible ? "opacity-[0.12] translate-y-0" : "opacity-0 translate-y-8"}`} style={{
      fontSize: "clamp(220px, 32vw, 380px)",
      lineHeight: 1,
      color: "var(--secondary)",
      fontFamily: "var(--font-serif)"
    }} aria-hidden="true">
        樹
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 pt-28 md:pt-32 pb-24 md:pb-20 min-h-screen flex flex-col">
        {}
        <div ref={metaRef} className={`flex justify-end mb-12 md:mb-16 transition-all duration-1000 ease-out ${metaVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}>
          <span className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground font-sans">
            一 Professional Arboriculture
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-12 lg:gap-16 items-center flex-1">
          {}
          <div className="flex flex-col">
            {}
            <div ref={headlineRef} className="mb-10 md:mb-12">
              <h1 className="font-default font-light leading-[0.95] tracking-tight text-foreground" style={{
              fontSize: "clamp(56px, 9vw, 120px)"
            }}>
                <span className="block">
                  {headlineWords.map((word, i) => <span key={i} data-index={i} className="inline-block mr-[0.25em] overflow-hidden align-bottom">
                      <span className={`inline-block transition-all ease-out`} style={{
                    transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
                    opacity: headlineVisible ? 1 : 0,
                    transitionDuration: "900ms",
                    transitionDelay: `${i * 80}ms`
                  }}>
                        {word}
                      </span>
                    </span>)}
                </span>
                <span className="block overflow-hidden">
                  <span className="inline-block italic transition-all ease-out" style={{
                  color: "var(--primary)",
                  transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
                  opacity: headlineVisible ? 1 : 0,
                  transitionDuration: "900ms",
                  transitionDelay: "320ms"
                }}>
                    Legacy
                  </span>
                </span>
              </h1>
            </div>

            {}
            <p ref={paragraphRef} className={`max-w-[480px] text-[17px] md:text-[18px] leading-[1.7] font-default text-muted-foreground mb-10 transition-all duration-1000 ease-out ${paragraphVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "500ms"
          }}>
              Dedicated to the health and longevity of your landscape, providing expert tree care with precision and respect for the environment.
            </p>

            {}
            <div ref={ctaRef} className={`flex flex-wrap items-center gap-6 md:gap-8 transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "650ms"
          }}>
              <Button asChild size="lg" className="rounded-lg px-7 h-12 bg-primary text-primary-foreground hover:bg-primary/90 text-[15px] font-default font-medium shadow-md">
                <Link to="/GetQuotePage">Request an Estimate</Link>
              </Button>

              <Link to="/OurExpertisePage" className="group inline-flex items-center gap-2 text-foreground text-[15px] font-default font-medium border-b border-foreground/40 hover:border-foreground pb-1 transition-colors">
                <span>View Our Services</span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative w-full transition-all duration-[1200ms] ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: "200ms"
        }}>
            <div className="relative overflow-hidden rounded-lg shadow-lg" style={{
            aspectRatio: "3 / 4"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_tree_care.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_tree_care.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arborist_tree_care.webp-wvc-sizes" alt="Professional arborist performing careful tree maintenance" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
              {}
              <div className="absolute inset-0 pointer-events-none" style={{
              background: "linear-gradient(180deg, hsla(30, 15%, 25%, 0.08) 0%, hsla(30, 15%, 15%, 0.12) 100%)"
            }} />
            </div>

            {}
            <div className="mt-5 flex items-center gap-3">
              <span className="h-px w-8 bg-foreground/30" />
              <span className="text-[11px] tracking-[0.2em] uppercase font-sans text-muted-foreground">
                Certified Arborist Care
              </span>
            </div>
          </div>
        </div>

        {}
        <div className="flex justify-center mt-12 md:mt-10">
          <div className="flex flex-col items-center gap-3">
            <span className="text-[10px] tracking-[0.3em] uppercase font-sans text-muted-foreground">
              Scroll
            </span>
            <div className="relative h-12 w-px overflow-hidden bg-foreground/15">
              <span className="absolute left-0 top-0 w-px bg-foreground" style={{
              height: "50%",
              animation: "pelagoScrollLine 2.2s ease-in-out infinite"
            }} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pelagoScrollLine {
          0% { transform: translateY(-100%); }
          50% { transform: translateY(0%); }
          100% { transform: translateY(200%); }
        }
      `}</style>
    </section>;
}